import { useSyncExternalStore } from 'react';

/**
 * Whole-UI display scale for reading at a distance (wall / iPad on a stand). Per-device.
 *
 * We zoom #root and shrink its coordinate box to viewport/scale, so the app still fits the
 * screen but is rendered larger — everything keeps its proportions and the content region just
 * scrolls when there's more of it. Applied via a `data-scale` attribute + CSS rules (see
 * index.css) so the blocking script can set it before first paint. Values must match those rules.
 */

export const SCALE_KEY = 'pacer-scale';

export const SCALES: Array<{ value: string; label: string }> = [
  { value: '1', label: 'Default' },
  { value: '1.15', label: 'Large' },
  { value: '1.3', label: 'Larger' },
];

let scale = read();
const listeners = new Set<() => void>();

function read(): string {
  try {
    const s = localStorage.getItem(SCALE_KEY);
    if (s && SCALES.some((x) => x.value === s)) return s;
  } catch {
    /* storage disabled */
  }
  return '1';
}

function apply(s: string): void {
  if (s === '1') delete document.documentElement.dataset.scale;
  else document.documentElement.dataset.scale = s;
}

export function setDisplayScale(s: string): void {
  scale = s;
  try {
    localStorage.setItem(SCALE_KEY, s);
  } catch {
    /* non-persistent is still usable this session */
  }
  apply(s);
  for (const l of listeners) l();
}

export function useDisplayScale(): string {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => scale,
    () => '1',
  );
}
